import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Badge,
  Button,
  buttonClass,
  Breadcrumb,
  Section,
} from "@/components/ui";
import { domainInitials, domainKeys, domainsApi, type Connection, type ConnectionStep } from "@/lib/domains";
import { siteHref } from "@/lib/format";
import {
  Heading,
  HeadTile,
  DomainPage,
  RuleRow,
  ProgressSection,
  Notice,
  type Step,
} from "@/components/domains/parts";

const FOOTNOTE =
  "Propagation is usually instant on CortexGrip-managed domains, but can occasionally take a few minutes.";

type Phase = "configuring" | "connected" | "failed";

function phaseOf(c: Connection): Phase {
  const verify = c.steps.find((s) => s.key === "verify")?.state;
  if (c.domain.connection === "connected" || verify === "completed") return "connected";
  if (c.domain.connection === "error" || c.steps.some((s) => s.state === "failed")) return "failed";
  return "configuring";
}

/** Figma "Domain configuring" (4072-1050) → "Domain connected" (4072-1296), or what went wrong. */
export function DomainConnectionPage() {
  const { domainId = "" } = useParams();
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const connection = useQuery({
    queryKey: [...domainKeys.detail(orgId, domainId), "connection"],
    queryFn: () => domainsApi.connection(orgId, domainId),
    refetchInterval: (q) => (q.state.data && phaseOf(q.state.data) !== "configuring" ? false : 3000),
  });
  const phase = connection.data ? phaseOf(connection.data) : null;
  // Once it settles, the list and the Control Centre are out of date.
  const settled = useRef<Phase | null>(null);
  useEffect(() => {
    if (!phase || phase === "configuring" || settled.current === phase) return;
    settled.current = phase;
    void qc.invalidateQueries({ queryKey: [...domainKeys.all(orgId), "list"] });
    void qc.invalidateQueries({ queryKey: domainKeys.detail(orgId, domainId), exact: true });
  }, [phase, qc, orgId, domainId]);
  const disconnect = useMutation({
    mutationFn: () => domainsApi.disconnect(orgId, domainId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      navigate(`/domains/${domainId}`);
    },
  });

  if (connection.isPending) return <FullPageSpinner />;
  if (connection.isError)
    return <ErrorState error={connection.error} onRetry={() => void connection.refetch()} />;
  const c = connection.data;
  const d = c.domain;
  if (!d.site_id || c.steps.length === 0) return <Navigate to={`/domains/${d.id}/connect`} replace />;

  const siteName = c.site?.name ?? d.site?.name;
  const linkScheme = (c.site ?? d.site)?.link_scheme;
  const steps: Step[] = c.steps.map((s) => ({ label: stepLabel(s, c), state: s.state }));
  const crumb = {
    configuring: "domain configuring",
    connected: "domain connected",
    failed: "connection failed",
  }[phase!];

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: d.display_name, to: `/domains/${d.id}` },
          { label: crumb },
        ]}
      />
      <Heading
        size={24}
        tile={<HeadTile tone="neutral">{domainInitials(d.fqdn)}</HeadTile>}
        title={d.display_name}
        status={
          phase === "connected" ? (
            <Badge size="md" tone="success">
              Connected
            </Badge>
          ) : phase === "failed" ? (
            <Badge size="md" tone="danger">
              Needs fix
            </Badge>
          ) : (
            <Badge size="md" tone="warning">
              Configuring
            </Badge>
          )
        }
        subtitle={
          phase === "connected"
            ? d.is_primary
              ? "Now your website's primary custom domain"
              : `Now pointing at ${siteName ? `${siteName}'s` : "your"} website`
            : phase === "failed"
              ? "We couldn't finish connecting this domain to your website"
              : `Connecting this domain to ${siteName ? `${siteName}'s` : "your"} website — this usually takes a minute`
        }
      />

      {phase === "configuring" && (
        <>
          <Notice tone="processing" title="Setting up your connection...">
            <p>
              We're automatically configuring the DNS records needed to point {d.display_name} at your
              website. You don't need to do anything.
            </p>
            {c.detail && <p className="mt-2 text-xs text-[#766e6a]">Latest check: {c.detail}</p>}
          </Notice>
          <ProgressSection title="Connection progress" steps={steps} footnote={FOOTNOTE} />
        </>
      )}

      {phase === "failed" && (
        <>
          <Notice
            tone="danger"
            title={`${d.display_name} isn't pointing at your website yet`}
            actions={
              <>
                <Button
                  size="lg"
                  variant="danger"
                  loading={connection.isFetching}
                  onClick={() => void connection.refetch()}
                >
                  Recheck now
                </Button>
                <Link to={`/domains/${d.id}?tab=dns`} className={buttonClass("secondary", "lg")}>
                  Open DNS settings
                </Link>
                <Button
                  size="lg"
                  variant="secondary"
                  requires="domains:manage"
                  loading={disconnect.isPending}
                  onClick={() => disconnect.mutate()}
                >
                  Disconnect
                </Button>
              </>
            }
          >
            {c.detail ??
              "We couldn't confirm that the domain resolves to your website. Check its DNS records, then recheck."}
          </Notice>
          <ErrorAlert error={disconnect.error} />
          <ProgressSection title="Connection progress" steps={steps} footnote={FOOTNOTE} />
        </>
      )}

      {phase === "connected" && (
        <>
          <Notice tone="success" title={`${d.display_name} is live`}>
            Visitors going to {d.display_name} now see your website. Your CortexGrip address keeps working
            too.
          </Notice>
          <Section title="Connection summary" className="w-full max-w-[575px]">
            <RuleRow first>
              <SummaryLabel>Website address</SummaryLabel>
              <Badge size="md" tone="success">
                {d.display_name}
              </Badge>
            </RuleRow>
            <RuleRow>
              <SummaryLabel>Primary domain</SummaryLabel>
              {d.is_primary ? (
                <Badge size="md" tone="success">
                  {d.display_name}
                </Badge>
              ) : (
                <Badge size="md" tone="neutral">
                  Unchanged
                </Badge>
              )}
            </RuleRow>
            {c.site && (
              <RuleRow>
                <SummaryLabel>Old subdomain</SummaryLabel>
                <Badge size="md" tone="neutral">
                  {c.site.hostname} · still works
                </Badge>
              </RuleRow>
            )}
            <RuleRow>
              <SummaryLabel>SSL certificate</SummaryLabel>
              {linkScheme === "http" ? (
                <Badge size="md" tone="neutral">
                  Not in use (http)
                </Badge>
              ) : (
                <Badge size="md" tone="success">
                  Issued automatically
                </Badge>
              )}
            </RuleRow>
            <div className="flex flex-wrap gap-2.5">
              <a
                href={siteHref(d.fqdn, linkScheme)}
                target="_blank"
                rel="noreferrer"
                className={buttonClass("brand", "lg")}
              >
                View website
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
              <Link to={`/domains/${d.id}`} className={buttonClass("secondary", "lg")}>
                Open Domain Control Centre
              </Link>
            </div>
          </Section>
        </>
      )}
    </DomainPage>
  );
}

function SummaryLabel({ children }: { children: string }) {
  return <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">{children}</p>;
}

function stepLabel(s: ConnectionStep, c: Connection): string {
  switch (s.key) {
    case "selected":
      return `Domain selected — ${c.domain.display_name}`;
    case "dns":
      return "DNS records configured automatically";
    case "verify":
      return "Verifying domain resolves to your website";
    case "primary":
      return c.domain.is_primary ? "Marked as primary domain" : "Primary domain left unchanged";
  }
}
